import { MySelector } from "../redux/store"
import logoDark from "../assets/itsForeverLogo.png"
import logoLight from "../assets/ife-logo-light.png"

/**
 * Resolves which logo the gallery should show.
 *
 * Phase 1 whitelabeling: when the event's host account is whitelabel-enabled
 * (EventDto.whitelabel === true) and a vendor on the event carries a logo, the
 * photographer's logo replaces the It's Forever logo. In every other case — non
 * whitelabel accounts, or before the event has loaded — we fall back to the
 * standard IFE logos.
 *
 * The photographer has a single logo (no dark/light variants), so the same
 * image is used for both themes; the IFE fallbacks keep their per-theme pair.
 */
export const useBrandLogo = () => {
  const { data } = MySelector((state) => state.event)
  const { theme } = MySelector((state) => state.theme)

  const event = data && data !== "null" ? data : undefined

  const vendorWithLogo = event?.whitelabel
    ? event?.vendors?.find(
        (v: any) => v?.compressedLogoLink || v?.logoLink
      )
    : undefined

  const photographerLogo =
    vendorWithLogo?.compressedLogoLink || vendorWithLogo?.logoLink

  const isWhitelabel = Boolean(photographerLogo)

  // A photographer logo is a single image reused across both themes, so it can
  // blend into a same-tone background (white logo on the light loader, dark logo
  // on the dark navbar). A soft shadow in the opposite tone keeps it legible.
  // The IFE logos are purpose-built per theme and need no shadow.
  const shadowClass = isWhitelabel
    ? theme === "dark"
      ? "drop-shadow-[0_1px_3px_rgba(255,255,255,0.35)]"
      : "drop-shadow-[0_1px_3px_rgba(0,0,0,0.30)]"
    : ""

  return {
    dark: photographerLogo || logoDark,
    light: photographerLogo || logoLight,
    isWhitelabel,
    shadowClass,
    // False during a cold load (event not fetched yet), when we can't know if
    // this gallery is whitelabelled. Callers use this to stay brand-neutral —
    // showing neither the IFE nor a photographer logo — until the brand resolves.
    brandKnown: Boolean(event),
  }
}
